// frontend/src/modules/Confirmations/components/PaymentManagementModal.tsx
import { useState, useEffect } from 'react';
import {
  Modal, Button, Stack, Group, Text, TextInput, Textarea, NumberInput,
  Select, Card, Grid, Tabs, ActionIcon, ThemeIcon, Badge, Progress,
  Loader, Center, Switch, Image, FileButton, Tooltip, Paper, Box
} from '@mantine/core';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { notifications } from '@mantine/notifications';
import { useMediaQuery } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import {
  IconPlus, IconTrash, IconEdit, IconCheck, IconX, IconUpload,
  IconCash, IconCreditCard, IconBuildingBank, IconCurrencyBitcoin,
  IconCalendar, IconBell, IconPhoto
} from '@tabler/icons-react';
import {
  confirmationsApi, Confirmation, ConfirmationPayment, ConfirmationReminder,
  PaymentSummary, ExpectedAmount
} from '@/api/confirmations.api';
import dayjs from 'dayjs';

const DATEPICKER_PORTAL_ID = 'payment-modal-datepicker-portal';
const CURRENCIES = ['THB', 'USD', 'EUR', 'RUB', 'CNY', 'GBP'];
const PAYMENT_METHODS = [
  { value: 'bank_transfer', label: 'Bank Transfer', icon: IconBuildingBank },
  { value: 'cash', label: 'Cash', icon: IconCash },
  { value: 'crypto', label: 'Crypto', icon: IconCurrencyBitcoin },
  { value: 'card', label: 'Card', icon: IconCreditCard },
  { value: 'other', label: 'Other', icon: IconCash },
];

interface Props {
  opened: boolean;
  onClose: () => void;
  confirmation: Confirmation;
  onStatusChange?: () => void;
}

const PaymentManagementModal = ({ opened, onClose, confirmation, onStatusChange }: Props) => {
  const isMobile = useMediaQuery('(max-width: 768px)');

  const [activeTab, setActiveTab] = useState<string | null>('overview');
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState<PaymentSummary | null>(null);
  const [payments, setPayments] = useState<ConfirmationPayment[]>([]);
  const [reminders, setReminders] = useState<ConfirmationReminder[]>([]);

  // Expected amounts
  const [editingExpected, setEditingExpected] = useState(false);
  const [expectedForm, setExpectedForm] = useState<ExpectedAmount[]>([{ amount: 0, currency: 'THB' }]);

  // Payment form
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [editingPaymentId, setEditingPaymentId] = useState<number | null>(null);
  const [paymentForm, setPaymentForm] = useState({ amount: 0, currency: 'THB', payment_date: new Date(), payment_method: 'bank_transfer', notes: '' });
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);

  // Reminder form
  const [showReminderForm, setShowReminderForm] = useState(false);
  const [editingReminderId, setEditingReminderId] = useState<number | null>(null);
  const [reminderForm, setReminderForm] = useState({
    reminder_type: 'days_before' as string,
    days_before: 7,
    custom_date: null as Date | null,
    comment: '',
    is_enabled: true,
    recurrence_type: 'none' as string,
    recurrence_interval: 1,
    recurrence_end_date: null as Date | null,
    recurrence_max_count: null as number | null,
  });

  useEffect(() => {
    let el = document.getElementById(DATEPICKER_PORTAL_ID);
    if (!el) {
      el = document.createElement('div');
      el.id = DATEPICKER_PORTAL_ID;
      document.body.appendChild(el);
    }
    el.style.position = 'relative';
    el.style.zIndex = '10000';
  }, []);

  useEffect(() => {
    if (opened && confirmation?.id) fetchAll();
  }, [opened, confirmation?.id]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [summaryRes, paymentsRes, remindersRes] = await Promise.all([
        confirmationsApi.getPaymentSummary(confirmation.id),
        confirmationsApi.getPayments(confirmation.id),
        confirmationsApi.getReminders(confirmation.id),
      ]);
      setSummary(summaryRes.data.data);
      setPayments(paymentsRes.data.data || []);
      setReminders(remindersRes.data.data || []);
    } catch (e) { console.error('Error fetching payment data:', e); }
    finally { setLoading(false); }
  };

  // =============== EXPECTED AMOUNTS ===============
  const startEditExpected = () => {
    const existing = summary?.expected_amounts || [];
    setExpectedForm(existing.length > 0
      ? existing.map(e => ({ amount: e.amount, currency: e.currency }))
      : [{ amount: 0, currency: 'THB' }]
    );
    setEditingExpected(true);
  };

  const saveExpectedAmounts = async () => {
    try {
      const valid = expectedForm.filter(e => e.amount > 0 && e.currency);
      await confirmationsApi.updateExpectedAmounts(confirmation.id, valid);
      setEditingExpected(false);
      fetchAll();
      onStatusChange?.();
    } catch (e) {
      notifications.show({ title: 'Error', message: 'Failed to save', color: 'red' });
    }
  };

  // =============== PAYMENTS ===============
  const resetPaymentForm = () => {
    setPaymentForm({ amount: 0, currency: 'THB', payment_date: new Date(), payment_method: 'bank_transfer', notes: '' });
    setPendingFiles([]);
    setEditingPaymentId(null);
    setShowPaymentForm(false);
  };

  const savePayment = async () => {
    if (!paymentForm.amount || paymentForm.amount <= 0) {
      notifications.show({ title: 'Validation', message: 'Amount must be greater than 0', color: 'orange' });
      return;
    }
    try {
      const data = {
        amount: paymentForm.amount,
        currency: paymentForm.currency,
        payment_date: dayjs(paymentForm.payment_date).format('YYYY-MM-DD'),
        payment_method: paymentForm.payment_method,
        notes: paymentForm.notes || undefined,
      };

      let paymentId = editingPaymentId;
      if (editingPaymentId) {
        await confirmationsApi.updatePayment(confirmation.id, editingPaymentId, data);
      } else {
        const res = await confirmationsApi.createPayment(confirmation.id, data);
        paymentId = res.data.data.id;
      }

      // Upload pending files
      if (pendingFiles.length > 0 && paymentId) {
        const formData = new FormData();
        pendingFiles.forEach(f => formData.append('files', f));
        await confirmationsApi.uploadPaymentFiles(confirmation.id, paymentId, formData);
      }

      resetPaymentForm();
      fetchAll();
      onStatusChange?.();
      notifications.show({ title: 'Success', message: editingPaymentId ? 'Payment updated' : 'Payment added', color: 'green' });
    } catch (e) {
      notifications.show({ title: 'Error', message: 'Failed to save payment', color: 'red' });
    }
  };

  const editPayment = (p: ConfirmationPayment) => {
    setPaymentForm({
      amount: p.amount,
      currency: p.currency,
      payment_date: new Date(p.payment_date),
      payment_method: p.payment_method,
      notes: p.notes || '',
    });
    setEditingPaymentId(p.id);
    setPendingFiles([]);
    setShowPaymentForm(true);
  };

  const deletePayment = (paymentId: number) => {
    modals.openConfirmModal({
      title: 'Delete Payment',
      children: <Text size="sm">Are you sure?</Text>,
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        await confirmationsApi.deletePayment(confirmation.id, paymentId);
        fetchAll();
        onStatusChange?.();
      }
    });
  };

  const uploadFilesToPayment = async (paymentId: number, files: File[]) => {
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    await confirmationsApi.uploadPaymentFiles(confirmation.id, paymentId, formData);
    fetchAll();
  };

  const deleteFile = async (paymentId: number, fileId: number) => {
    await confirmationsApi.deletePaymentFile(confirmation.id, paymentId, fileId);
    fetchAll();
  };

  // =============== REMINDERS ===============
  const resetReminderForm = () => {
    setReminderForm({
      reminder_type: 'days_before', days_before: 7, custom_date: null,
      comment: '', is_enabled: true, recurrence_type: 'none',
      recurrence_interval: 1, recurrence_end_date: null, recurrence_max_count: null,
    });
    setEditingReminderId(null);
    setShowReminderForm(false);
  };

  const saveReminder = async () => {
    try {
      const data: any = {
        reminder_type: reminderForm.reminder_type,
        days_before: reminderForm.reminder_type === 'days_before' ? reminderForm.days_before : null,
        custom_date: reminderForm.reminder_type === 'custom_date' && reminderForm.custom_date ? dayjs(reminderForm.custom_date).format('YYYY-MM-DD') : null,
        comment: reminderForm.comment || null,
        is_enabled: reminderForm.is_enabled,
        recurrence_type: reminderForm.recurrence_type,
        recurrence_interval: reminderForm.recurrence_type !== 'none' ? reminderForm.recurrence_interval : null,
        recurrence_end_date: reminderForm.recurrence_end_date ? dayjs(reminderForm.recurrence_end_date).format('YYYY-MM-DD') : null,
        recurrence_max_count: reminderForm.recurrence_max_count || null,
      };

      if (editingReminderId) {
        await confirmationsApi.updateReminder(confirmation.id, editingReminderId, data);
      } else {
        await confirmationsApi.createReminder(confirmation.id, data);
      }

      resetReminderForm();
      fetchAll();
      notifications.show({ title: 'Success', message: editingReminderId ? 'Reminder updated' : 'Reminder created', color: 'green' });
    } catch (e) {
      notifications.show({ title: 'Error', message: 'Failed to save reminder', color: 'red' });
    }
  };

  const editReminder = (r: ConfirmationReminder) => {
    setReminderForm({
      reminder_type: r.reminder_type,
      days_before: r.days_before || 7,
      custom_date: r.custom_date ? new Date(r.custom_date) : null,
      comment: r.comment || '',
      is_enabled: !!r.is_enabled,
      recurrence_type: r.recurrence_type || 'none',
      recurrence_interval: r.recurrence_interval || 1,
      recurrence_end_date: r.recurrence_end_date ? new Date(r.recurrence_end_date) : null,
      recurrence_max_count: r.recurrence_max_count || null,
    });
    setEditingReminderId(r.id);
    setShowReminderForm(true);
  };

  const deleteReminder = (reminderId: number) => {
    modals.openConfirmModal({
      title: 'Delete Reminder',
      children: <Text size="sm">Are you sure?</Text>,
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        await confirmationsApi.deleteReminder(confirmation.id, reminderId);
        fetchAll();
      }
    });
  };

  const quickAddReminder = async (type: string, days?: number) => {
    try {
      await confirmationsApi.createReminder(confirmation.id, {
        reminder_type: type,
        days_before: days || null,
        is_enabled: true,
        recurrence_type: 'none',
      });
      fetchAll();
      notifications.show({ title: 'Success', message: 'Reminder added', color: 'green' });
    } catch (e) {
      notifications.show({ title: 'Error', message: 'Failed', color: 'red' });
    }
  };

  const toggleReminder = async (r: ConfirmationReminder) => {
    await confirmationsApi.updateReminder(confirmation.id, r.id, { is_enabled: !r.is_enabled });
    fetchAll();
  };

  const getMethodIcon = (method: string) => {
    const m = PAYMENT_METHODS.find(pm => pm.value === method);
    return m ? <m.icon size={16} /> : <IconCash size={16} />;
  };

  const nights = confirmation.arrival_date && confirmation.departure_date
    ? dayjs(confirmation.departure_date).diff(dayjs(confirmation.arrival_date), 'day') : 0;

  return (
    <Modal opened={opened} onClose={onClose} title={
      <Group gap="sm">
        <ThemeIcon size="lg" variant="gradient" gradient={{ from: 'teal', to: 'cyan' }}><IconCash size={20} /></ThemeIcon>
        <Text fw={600}>Payment & Reminders</Text>
      </Group>
    } size="xl" fullScreen={isMobile}>
      {loading ? <Center p="xl"><Loader /></Center> : (
        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value="overview" leftSection={<IconCash size={14} />}>Overview</Tabs.Tab>
            <Tabs.Tab value="payments" leftSection={<IconCreditCard size={14} />}>
              Payments {payments.length > 0 && <Badge size="xs" ml={4}>{payments.length}</Badge>}
            </Tabs.Tab>
            <Tabs.Tab value="reminders" leftSection={<IconBell size={14} />}>
              Reminders {reminders.length > 0 && <Badge size="xs" ml={4}>{reminders.length}</Badge>}
            </Tabs.Tab>
          </Tabs.List>

          {/* OVERVIEW TAB */}
          <Tabs.Panel value="overview" pt="md">
            <Stack gap="md">
              {/* Booking Info */}
              <Card withBorder p="md">
                <Text fw={600} size="sm" mb="sm">Booking Info</Text>
                <Grid>
                  <Grid.Col span={6}><Text size="xs" c="dimmed">Property</Text><Text size="sm" fw={500}>{confirmation.property_name || '—'}</Text></Grid.Col>
                  <Grid.Col span={3}><Text size="xs" c="dimmed">Dates</Text><Text size="sm">{confirmation.arrival_date ? `${dayjs(confirmation.arrival_date).format('DD.MM')} - ${dayjs(confirmation.departure_date).format('DD.MM')}` : '—'}</Text></Grid.Col>
                  <Grid.Col span={3}><Text size="xs" c="dimmed">Nights</Text><Text size="sm" fw={500}>{nights}</Text></Grid.Col>
                </Grid>
              </Card>

              {/* Expected Amounts */}
              <Card withBorder p="md">
                <Group justify="space-between" mb="sm">
                  <Text fw={600} size="sm">Expected Amounts</Text>
                  <Button size="xs" variant="light" onClick={startEditExpected}><IconEdit size={14} /></Button>
                </Group>

                {editingExpected ? (
                  <Stack gap="sm">
                    {expectedForm.map((ea, idx) => (
                      <Group key={idx} gap="sm">
                        <NumberInput flex={1} value={ea.amount} onChange={v => { const u = [...expectedForm]; u[idx].amount = Number(v) || 0; setExpectedForm(u); }} min={0} size="sm" />
                        <Select w={100} value={ea.currency} onChange={v => { const u = [...expectedForm]; u[idx].currency = v || 'THB'; setExpectedForm(u); }} data={CURRENCIES} size="sm" />
                        {expectedForm.length > 1 && <ActionIcon color="red" variant="light" onClick={() => setExpectedForm(expectedForm.filter((_, i) => i !== idx))}><IconTrash size={14} /></ActionIcon>}
                      </Group>
                    ))}
                    <Group>
                      <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} onClick={() => setExpectedForm([...expectedForm, { amount: 0, currency: 'THB' }])}>Add Currency</Button>
                      <Button size="xs" color="green" onClick={saveExpectedAmounts}>Save</Button>
                      <Button size="xs" variant="subtle" onClick={() => setEditingExpected(false)}>Cancel</Button>
                    </Group>
                  </Stack>
                ) : (
                  <Stack gap="xs">
                    {summary?.currency_statuses && summary.currency_statuses.length > 0 ? (
                      summary.currency_statuses.map((cs, idx) => (
                        <div key={idx}>
                          <Group justify="space-between" mb={4}>
                            <Text size="sm">{cs.currency}</Text>
                            <Text size="sm" fw={500}>{cs.paid.toLocaleString()} / {cs.expected.toLocaleString()}</Text>
                          </Group>
                          <Progress
                            value={cs.expected > 0 ? Math.min(100, (cs.paid / cs.expected) * 100) : 0}
                            color={cs.is_fully_paid ? 'green' : cs.paid > 0 ? 'orange' : 'gray'}
                            size="lg" radius="md" striped={!cs.is_fully_paid && cs.paid > 0} animated={!cs.is_fully_paid && cs.paid > 0}
                          />
                          {cs.remaining > 0 && <Text size="xs" c="dimmed" mt={2}>Remaining: {cs.remaining.toLocaleString()} {cs.currency}</Text>}
                        </div>
                      ))
                    ) : (
                      <Text size="sm" c="dimmed">No expected amounts set</Text>
                    )}
                  </Stack>
                )}
              </Card>
            </Stack>
          </Tabs.Panel>

          {/* PAYMENTS TAB */}
          <Tabs.Panel value="payments" pt="md">
            <Stack gap="md">
              <Group justify="flex-end">
                <Button size="sm" variant="gradient" gradient={{ from: 'teal', to: 'cyan' }} leftSection={<IconPlus size={16} />}
                  onClick={() => { resetPaymentForm(); setShowPaymentForm(true); }}>
                  Add Payment
                </Button>
              </Group>

              {/* Payment Form */}
              {showPaymentForm && (
                <Card withBorder p="md">
                  <Text fw={600} size="sm" mb="sm">{editingPaymentId ? 'Edit Payment' : 'New Payment'}</Text>
                  <Grid>
                    <Grid.Col span={{ base: 6, sm: 4 }}>
                      <NumberInput label="Amount" value={paymentForm.amount} onChange={v => setPaymentForm({ ...paymentForm, amount: Number(v) || 0 })} min={0} required size="sm" />
                    </Grid.Col>
                    <Grid.Col span={{ base: 6, sm: 3 }}>
                      <Select label="Currency" value={paymentForm.currency} onChange={v => setPaymentForm({ ...paymentForm, currency: v || 'THB' })} data={CURRENCIES} size="sm" />
                    </Grid.Col>
                    <Grid.Col span={{ base: 6, sm: 5 }}>
                      <Text size="sm" fw={500} mb={4}>Date</Text>
                      <DatePicker selected={paymentForm.payment_date} onChange={(d: Date | null) => setPaymentForm({ ...paymentForm, payment_date: d || new Date() })}
                        dateFormat="dd/MM/yyyy" portalId={DATEPICKER_PORTAL_ID}                        customInput={<TextInput size="sm" leftSection={<IconCalendar size={14} />} readOnly style={{ cursor: 'pointer' }} />} />
                    </Grid.Col>
                    <Grid.Col span={{ base: 6, sm: 4 }}>
                      <Select label="Method" value={paymentForm.payment_method}
                        onChange={v => setPaymentForm({ ...paymentForm, payment_method: v || 'bank_transfer' })}
                        data={PAYMENT_METHODS.map(m => ({ value: m.value, label: m.label }))} size="sm" />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 8 }}>
                      <Textarea label="Notes" value={paymentForm.notes} onChange={e => setPaymentForm({ ...paymentForm, notes: e.target.value })} size="sm" minRows={1} />
                    </Grid.Col>
                    <Grid.Col span={12}>
                      <Group gap="sm">
                        <FileButton onChange={(files) => setPendingFiles(files)} accept="image/*,application/pdf" multiple>
                          {(props) => <Button {...props} size="xs" variant="light" leftSection={<IconUpload size={14} />}>Attach Receipt</Button>}
                        </FileButton>
                        {pendingFiles.length > 0 && <Badge size="sm">{pendingFiles.length} file(s) selected</Badge>}
                      </Group>
                    </Grid.Col>
                  </Grid>
                  <Group mt="md" justify="flex-end">
                    <Button variant="subtle" size="sm" onClick={resetPaymentForm}>Cancel</Button>
                    <Button color="green" size="sm" leftSection={<IconCheck size={14} />} onClick={savePayment}>
                      {editingPaymentId ? 'Update' : 'Save'}
                    </Button>
                  </Group>
                </Card>
              )}

              {/* Payments List */}
              {payments.length === 0 ? (
                <Center p="lg"><Text c="dimmed" size="sm">No payments recorded</Text></Center>
              ) : (
                <Stack gap="sm">
                  {payments.map(p => (
                    <Card key={p.id} withBorder p="sm">
                      <Group justify="space-between" wrap="nowrap">
                        <Group gap="sm">
                          {getMethodIcon(p.payment_method)}
                          <div>
                            <Text size="sm" fw={600}>{p.amount.toLocaleString()} {p.currency}</Text>
                            <Text size="xs" c="dimmed">{dayjs(p.payment_date).format('DD.MM.YYYY')} · {p.payment_method}</Text>
                            {p.notes && <Text size="xs" c="dimmed">{p.notes}</Text>}
                            {p.created_by_name && <Text size="xs" c="dimmed">by {p.created_by_name}</Text>}
                          </div>
                        </Group>
                        <Group gap={4}>
                          <FileButton onChange={(files) => files.length > 0 && uploadFilesToPayment(p.id, files)} accept="image/*,application/pdf" multiple>
                            {(props) => (
                              <Tooltip label="Upload receipt">
                                <ActionIcon {...props} variant="light" size="sm"><IconUpload size={14} /></ActionIcon>
                              </Tooltip>
                            )}
                          </FileButton>
                          <ActionIcon variant="light" size="sm" onClick={() => editPayment(p)}><IconEdit size={14} /></ActionIcon>
                          <ActionIcon variant="light" color="red" size="sm" onClick={() => deletePayment(p.id)}><IconTrash size={14} /></ActionIcon>
                        </Group>
                      </Group>

                      {/* Files */}
                      {p.files && p.files.length > 0 && (
                        <Group gap="xs" mt="xs" wrap="wrap">
                          {p.files.map(f => (
                            <Paper key={f.id} withBorder p={4} radius="sm" style={{ position: 'relative' }}>
                              {f.mime_type?.startsWith('image/') ? (
                                <Image src={f.file_path} alt={f.file_name} w={60} h={60} radius="sm" fit="cover"
                                  style={{ cursor: 'pointer' }} onClick={() => window.open(f.file_path, '_blank')} />
                              ) : (
                                <Box w={60} h={60} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                                  onClick={() => window.open(f.file_path, '_blank')}>
                                  <IconPhoto size={24} />
                                </Box>
                              )}
                              <ActionIcon size={16} color="red" variant="filled" radius="xl"
                                style={{ position: 'absolute', top: -4, right: -4 }}
                                onClick={() => deleteFile(p.id, f.id)}>
                                <IconX size={10} />
                              </ActionIcon>
                            </Paper>
                          ))}
                        </Group>
                      )}
                    </Card>
                  ))}
                </Stack>
              )}
            </Stack>
          </Tabs.Panel>

          {/* REMINDERS TAB */}
          <Tabs.Panel value="reminders" pt="md">
            <Stack gap="md">
              {/* Quick Add */}
              <Card withBorder p="md">
                <Text fw={600} size="sm" mb="sm">Quick Add</Text>
                <Group gap="xs" wrap="wrap">
                  <Button size="xs" variant="light" onClick={() => quickAddReminder('check_in_day')}>Check-in Day</Button>
                  <Button size="xs" variant="light" onClick={() => quickAddReminder('days_before', 3)}>3 Days Before</Button>
                  <Button size="xs" variant="light" onClick={() => quickAddReminder('days_before', 7)}>7 Days Before</Button>
                  <Button size="xs" variant="light" onClick={() => quickAddReminder('days_before', 14)}>14 Days Before</Button>
                  <Button size="xs" variant="light" onClick={() => quickAddReminder('days_before', 30)}>30 Days Before</Button>
                </Group>
              </Card>

              {/* Add/Edit Reminder Form */}
              <Group justify="flex-end">
                <Button size="sm" variant="gradient" gradient={{ from: 'teal', to: 'cyan' }} leftSection={<IconPlus size={16} />}
                  onClick={() => { resetReminderForm(); setShowReminderForm(true); }}>
                  Custom Reminder
                </Button>
              </Group>

              {showReminderForm && (
                <Card withBorder p="md">
                  <Text fw={600} size="sm" mb="sm">{editingReminderId ? 'Edit Reminder' : 'New Reminder'}</Text>
                  <Stack gap="sm">
                    <Select label="Trigger Type" value={reminderForm.reminder_type}
                      onChange={v => setReminderForm({ ...reminderForm, reminder_type: v || 'days_before' })}
                      data={[
                        { value: 'check_in_day', label: 'On Check-in Day' },
                        { value: 'days_before', label: 'Days Before Check-in' },
                        { value: 'custom_date', label: 'Custom Date' },
                      ]} size="sm" />

                    {reminderForm.reminder_type === 'days_before' && (
                      <NumberInput label="Days Before" value={reminderForm.days_before}
                        onChange={v => setReminderForm({ ...reminderForm, days_before: Number(v) || 1 })} min={1} max={365} size="sm" />
                    )}

                    {reminderForm.reminder_type === 'custom_date' && (
                      <>
                        <Text size="sm" fw={500} mb={-8}>Date</Text>
                        <DatePicker selected={reminderForm.custom_date}
                          onChange={(d: Date | null) => setReminderForm({ ...reminderForm, custom_date: d })}
                          dateFormat="dd/MM/yyyy" portalId={DATEPICKER_PORTAL_ID}                          customInput={<TextInput size="sm" leftSection={<IconCalendar size={14} />} readOnly style={{ cursor: 'pointer' }} />} />
                      </>
                    )}

                    <Textarea label="Comment" value={reminderForm.comment}
                      onChange={e => setReminderForm({ ...reminderForm, comment: e.target.value })} size="sm" minRows={1} />

                    <Select label="Recurrence" value={reminderForm.recurrence_type}
                      onChange={v => setReminderForm({ ...reminderForm, recurrence_type: v || 'none' })}
                      data={[
                        { value: 'none', label: 'One-time' },
                        { value: 'every_n_days', label: 'Every N days' },
                        { value: 'weekly', label: 'Weekly' },
                        { value: 'every_n_weeks', label: 'Every N weeks' },
                        { value: 'monthly_by_date', label: 'Monthly (same date)' },
                        { value: 'every_n_months', label: 'Every N months' },
                        { value: 'yearly', label: 'Yearly' },
                      ]} size="sm" />

                    {['every_n_days', 'every_n_weeks', 'every_n_months'].includes(reminderForm.recurrence_type) && (
                      <NumberInput label="Interval" value={reminderForm.recurrence_interval}
                        onChange={v => setReminderForm({ ...reminderForm, recurrence_interval: Number(v) || 1 })} min={1} size="sm" />
                    )}

                    {reminderForm.recurrence_type !== 'none' && (
                      <NumberInput label="Max Send Count (optional)" value={reminderForm.recurrence_max_count ?? undefined}
                        onChange={v => setReminderForm({ ...reminderForm, recurrence_max_count: v ? Number(v) : null })} min={1} size="sm"
                        placeholder="Unlimited" />
                    )}

                    <Switch label="Enabled" checked={reminderForm.is_enabled}
                      onChange={e => setReminderForm({ ...reminderForm, is_enabled: e.currentTarget.checked })} />

                    <Group justify="flex-end">
                      <Button variant="subtle" size="sm" onClick={resetReminderForm}>Cancel</Button>
                      <Button color="green" size="sm" leftSection={<IconCheck size={14} />} onClick={saveReminder}>
                        {editingReminderId ? 'Update' : 'Create'}
                      </Button>
                    </Group>
                  </Stack>
                </Card>
              )}

              {/* Reminders List */}
              {reminders.length === 0 ? (
                <Center p="lg"><Text c="dimmed" size="sm">No reminders set</Text></Center>
              ) : (
                <Stack gap="sm">
                  {reminders.map(r => (
                    <Card key={r.id} withBorder p="sm">
                      <Group justify="space-between" wrap="nowrap">
                        <Group gap="sm">
                          <ThemeIcon size="sm" variant="light" color={r.is_enabled ? 'teal' : 'gray'}>
                            <IconBell size={14} />
                          </ThemeIcon>
                          <div>
                            <Group gap={4}>
                              <Badge size="xs" variant="light" color="blue">
                                {r.reminder_type === 'check_in_day' ? 'Check-in Day' :
                                 r.reminder_type === 'days_before' ? `${r.days_before}d before` :
                                 `Custom: ${dayjs(r.custom_date).format('DD.MM.YY')}`}
                              </Badge>
                              {r.recurrence_type !== 'none' && (
                                <Badge size="xs" variant="light" color="violet">{r.recurrence_type.replace(/_/g, ' ')}</Badge>
                              )}
                              {r.is_sent && <Badge size="xs" color="green">Sent</Badge>}
                            </Group>
                            {r.next_send_date && !r.is_sent && (
                              <Text size="xs" c="dimmed">Next: {dayjs(r.next_send_date).format('DD.MM.YYYY')}</Text>
                            )}
                            {r.comment && <Text size="xs" c="dimmed">{r.comment}</Text>}
                            {r.recurrence_sent_count ? <Text size="xs" c="dimmed">Sent {r.recurrence_sent_count} times</Text> : null}
                          </div>
                        </Group>
                        <Group gap={4}>
                          <Switch size="xs" checked={!!r.is_enabled} onChange={() => toggleReminder(r)} />
                          <ActionIcon variant="light" size="sm" onClick={() => editReminder(r)}><IconEdit size={14} /></ActionIcon>
                          <ActionIcon variant="light" color="red" size="sm" onClick={() => deleteReminder(r.id)}><IconTrash size={14} /></ActionIcon>
                        </Group>
                      </Group>
                    </Card>
                  ))}
                </Stack>
              )}
            </Stack>
          </Tabs.Panel>
        </Tabs>
      )}
    </Modal>
  );
};

export default PaymentManagementModal;
